import { apiVss } from '../api/axiosClient';
import { API_BASES } from '../config/constants';

/**
 * Returns color and tooltip string based on accessMode
 */
export const getDeviceStatusInfo = (accessMode) => {
  if (accessMode === undefined || accessMode === null) return { color: 'disabled', tooltip: 'Checking Status...' };
  switch (Number(accessMode)) {
    case -1:
      return { color: 'error', tooltip: 'Offline' };
    case 0:
      return { color: 'disabled', tooltip: 'Unknown' };
    case 1:
      return { color: '#01FF05', tooltip: 'Online (Ethernet)' };
    case 2:
      return { color: '#01FF05', tooltip: 'Online (WIFI)' };
    case 3:
      return { color: '#01FF05', tooltip: 'Online (2G)' };
    case 4:
      return { color: '#01FF05', tooltip: 'Online (3G)' };
    case 5:
      return { color: '#01FF05', tooltip: 'Online (4G)' };
    case 6:
      return { color: '#01FF05', tooltip: 'Online (5G)' };
    case 7:
      return { color: '#01FF05', tooltip: 'Online (WIFI+3/4/5G)' };
    default:
      return { color: 'disabled', tooltip: 'Unknown' };
  }
};

let cachedHowenToken = null;
let tokenExpiresAt = 0;

/**
 * Retrieves Howen VSS token with 10s timeout and 10min memory cache.
 */
export const getHowenToken = async (timeoutMs = 10000) => {
  let token = cachedHowenToken;

  if (!token || Date.now() >= tokenExpiresAt) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    try {
      const tokenResp = await fetch(`${API_BASES.HOWEN_API}/get-token`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!tokenResp.ok) throw new Error(`Failed to fetch token (status ${tokenResp.status})`);
      const tokenData = await tokenResp.json();
      token = tokenData?.data?.token || tokenData?.token;
      if (!token) throw new Error('No token found');

      // Cache token for 10 minutes
      cachedHowenToken = token;
      tokenExpiresAt = Date.now() + 10 * 60 * 1000;
    } catch (tokenErr) {
      clearTimeout(timeoutId);
      if (tokenErr.name === 'AbortError') {
        console.warn(`[vssHelper] get-token Howen di-abort karena melebihi batas waktu ${timeoutMs / 1000} detik`);
      } else {
        console.warn('[vssHelper] Gagal mengambil token Howen:', tokenErr?.message || tokenErr);
      }
      return null;
    }
  }

  return token;
};

/**
 * Fetches VSS device status for an array of IMEIs.
 * Returns a map: { [imei]: { accessMode, lastOnlineTime, lastOfflineTime } }
 */
export const fetchVssDeviceStatusBulk = async (imeiListArray, timeoutMs = 10000) => {
  if (!imeiListArray || imeiListArray.length === 0) return {};

  try {
    const token = await getHowenToken(timeoutMs);
    if (!token) return {};

    // 2. Query Device Status (chunking just in case there are hundreds)
    const statusMap = {};
    const chunkSize = 150;

    for (let i = 0; i < imeiListArray.length; i += chunkSize) {
      const chunk = imeiListArray.slice(i, i + chunkSize);
      const imeiListStr = chunk.join(',');

      try {
        const queryController = new AbortController();
        const queryTimeoutId = setTimeout(() => {
          queryController.abort();
        }, timeoutMs);

        const resp = await apiVss.post(
          '/onoffline/queryMoreDeviceStatus.action',
          {
            token: String(token),
            deviceID: imeiListStr
          },
          { signal: queryController.signal }
        );

        clearTimeout(queryTimeoutId);

        const items = resp?.data?.data || resp?.data || [];
        if (Array.isArray(items)) {
          items.forEach((d) => {
            if (d.deviceID) {
              statusMap[d.deviceID] = {
                accessMode: d.accessMode,
                lastOnlineTime: d.lastOnlineTime,
                lastOfflineTime: d.lastOfflineTime
              };
            }
          });
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          console.warn(`[vssHelper] queryMoreDeviceStatus di-abort karena timeout ${timeoutMs / 1000}s`);
        } else {
          console.error('Failed to fetch chunk for VSS status', err);
        }
      }
    }

    return statusMap;
  } catch (err) {
    console.error('fetchVssDeviceStatusBulk error:', err);
    return {};
  }
};
